import React, { ChangeEvent, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  IconButton,
  useTheme
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { KeyboardArrowUp, KeyboardArrowDown, Close } from '@material-ui/icons';
import Table from 'components/@common/Table/StyledTable';
import fp from 'lodash/fp';
import useProductCache from 'store/cache/product-cache';
import NoAvailableLicensesMessage from 'components/@common/ProductLicenses/NoAvailableLicensesMessage';
import { useProductCardValidation } from 'components/@common/ProductLicenses/product-card-validation-provider';
import { LicenseAction, OrganizationLicenseUsage, ProductType } from 'types/__generated__/graphql';
import { Student, ProductCurriculum, Product } from 'types/schema-generated-types';
import { getFullName } from 'utilities/name-utils';
import { getProductNameAndIcon, getStudentCurriculumDisplayName } from 'utilities/product-utils';
import AssignAllStudentsButton from './AssignAllStudentsButton';
import LicenseAssignmentStatus from './LicenseAssignmentStatus';
import StudentCurriculumSelect from './StudentCurriculumSelect';
import UnsavedChangeIcon from './UnsavedChangeIcon';
import getPendingUpdateResult, { PendingUpdateResult } from './util/get-pending-update-result';

export interface LicenseUpdate {
  productFamilyId: number;
  productCurriculumId: number;
  studentId: number;
  action: LicenseAction;
}

interface ClassroomProductLicenseCardProps {
  productFamilyId: number;
  assignable?: boolean;
  organizationLicenseUsage: OrganizationLicenseUsage[];
  students?: Student[];
  assignedProductCurriculumMap?: Map<number, number>;
  pendingUpdates?: LicenseUpdate[];
  error?: boolean;
  onLicenseUnassign: (
    productFamilyId: number,
    productCurriculumId: number,
    studentId: number
  ) => void;
  onLicenseAssign: (
    productFamilyId: number,
    productCurriculumId: number,
    studentId: number
  ) => void;
}

interface LicenseCountProps {
  product: Product;
  singleProduct?: boolean;
  pendingUpdateResult: PendingUpdateResult;
}

interface StudentRowData {
  id: number;
  firstName: string;
  lastName: string;
  gradeLevel: string;
  assignedProductCurriculumId?: number | '';
  showCurriculumSelect: boolean;
  hasPendingUpdate: boolean;
  index: number;
}

interface StudentLicenseTableProps {
  productFamilyId: number;
  products: Product[];
  productCurriculums: ProductCurriculum[];
  disabledProductIds?: number[];
  assignable: boolean;
  canAssign: boolean;
  onLicenseUnassign: (
    productFamilyId: number,
    productCurriculumId: number | '',
    studentId: number
  ) => void;
  onLicenseAssign: (
    productFamilyId: number,
    productCurriculumId: number | '',
    studentId: number
  ) => void;
  onCurriculumSelect: (
    e: ChangeEvent<{ name?: string | undefined; value: string }>,
    studentId: number
  ) => void;
  showCurriculumSelect: (studentId: number) => void;
  hideCurriculumSelect: (studentId: number) => void;
  data: StudentRowData[];
}

// @ts-ignore
const map = fp.map.convert({ cap: false });

const commonCellStyles = (theme: Theme) => ({
  background: 'inherit',
  borderBottom: 'none',
  height: theme.typography.pxToRem(56),
  padding: theme.typography.pxToRem(4)
});

const StudentLicenseTable = ({
  productFamilyId,
  products,
  productCurriculums,
  disabledProductIds,
  assignable,
  canAssign,
  onLicenseAssign,
  onLicenseUnassign,
  onCurriculumSelect,
  showCurriculumSelect,
  data
}: StudentLicenseTableProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation('ClassroomProductLicenseCard');

  return (
    <Table
      data={data}
      options={{
        headerStyle: {
          fontWeight: 400,
          textTransform: 'uppercase',
          height: theme.typography.pxToRem(32),
          padding: theme.typography.pxToRem(4)
        },
        rowStyle: x => ({
          background: x.index % 2 ? '#FFFFFF' : '#F8F8F8',
          height: theme.typography.pxToRem(48)
        })
      }}
      columns={[
        {
          cellStyle: { ...commonCellStyles(theme), paddingLeft: 45 },
          headerStyle: { borderLeft: 'none', paddingLeft: 45 },
          title: t('Name'),
          render: x => (
            <>
              <div className={classes.changeIcon}>
                {x.hasPendingUpdate && <UnsavedChangeIcon />}
              </div>
              {getFullName({
                firstName: x.firstName,
                lastName: x.lastName
              })}
            </>
          )
        },
        {
          cellStyle: { ...commonCellStyles(theme) },
          title: t('Grade'),
          render: x => x.gradeLevel
        },
        {
          cellStyle: { ...commonCellStyles(theme) },
          title: t('License'),
          render: x => <LicenseAssignmentStatus assigned={!!x.assignedProductCurriculumId} />
        },
        {
          cellStyle: { ...commonCellStyles(theme) },
          title: t('Product'),
          render: x => {
            if (x.assignedProductCurriculumId && productCurriculums.length === 1) {
              const curriculum = productCurriculums[0];
              if (curriculum.id !== x.assignedProductCurriculumId) return null;

              return getStudentCurriculumDisplayName(curriculum);
            }

            if (x.showCurriculumSelect) {
              return (
                <StudentCurriculumSelect
                  productFamilyId={productFamilyId}
                  products={products}
                  disabledProductIds={disabledProductIds}
                  onChange={e => onCurriculumSelect(e, x.id)}
                  value={x.assignedProductCurriculumId}
                />
              );
            }
          }
        },
        {
          cellStyle: { ...commonCellStyles(theme), textAlign: 'center' },
          render: x => {
            if (!assignable) return null;

            const isShowAssignButton = !x.assignedProductCurriculumId && !x.showCurriculumSelect;
            return isShowAssignButton ? (
              <Button
                aria-label="Assign student license button"
                size="small"
                color="primary"
                variant="contained"
                disableElevation
                className={cx(classes.assignButton, !canAssign && classes.assignButtonDisabled)}
                disabled={!canAssign}
                onClick={() => {
                  if (productCurriculums.length === 1) {
                    onLicenseAssign(productFamilyId, productCurriculums[0].id, x.id);
                  } else {
                    showCurriculumSelect(x.id);
                    const gradeLevelCurriculums = productCurriculums.filter(c =>
                      c.gradeLevel?.includes(x.gradeLevel)
                    );

                    const productCurriculumId =
                      gradeLevelCurriculums.length === 1 ? gradeLevelCurriculums[0].id : '';

                    onLicenseAssign(productFamilyId, productCurriculumId, x.id);
                  }
                }}
              >
                {t('assign')}
              </Button>
            ) : (
              <Button
                aria-label="Unassign student license button"
                size="small"
                color="primary"
                variant="contained"
                disableElevation
                className={cx(classes.assignButton)}
                onClick={() => {
                  onLicenseUnassign(productFamilyId, x.assignedProductCurriculumId ?? '', x.id);
                }}
              >
                {t('unassign')}
              </Button>
            );
          }
        }
      ]}
    />
  );
};

const ClassroomProductLicenseCard = ({
  productFamilyId,
  assignable = false,
  organizationLicenseUsage,
  students = [],
  assignedProductCurriculumMap = new Map<number, number>(),
  pendingUpdates = [],
  error = false,
  onLicenseAssign,
  onLicenseUnassign
}: ClassroomProductLicenseCardProps) => {
  const classes = useStyles();
  const { t } = useTranslation('ClassroomProductLicenseCard');
  const [{ findProductsByProductFamilyIdAndType, findProductFamilyById }] = useProductCache();
  const [, { addInvalidProductFamilyId, clearInvalidProductFamilyId }] = useProductCardValidation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [unselectedCurriculums, setUnselectedCurriculums] = useState<number[]>([]);

  useEffect(() => {
    if (error && containerRef.current) containerRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [error]);

  useEffect(() => {
    if (unselectedCurriculums.length > 0) {
      addInvalidProductFamilyId(productFamilyId);
    } else {
      clearInvalidProductFamilyId(productFamilyId);
    }
  }, [
    addInvalidProductFamilyId,
    clearInvalidProductFamilyId,
    productFamilyId,
    unselectedCurriculums
  ]);

  const pendingAssignedProductCurriculumMap = new Map<number, number>(assignedProductCurriculumMap);
  pendingUpdates.forEach(u => {
    if (u.action === LicenseAction.ASSIGN) {
      pendingAssignedProductCurriculumMap.set(u.studentId, u.productCurriculumId);
    } else if (u.action === LicenseAction.UNASSIGN) {
      pendingAssignedProductCurriculumMap.delete(u.studentId);
    }
  });

  const [showSelectStudentIds, setShowSelectStudentIds] = useState<number[]>([
    ...pendingAssignedProductCurriculumMap.keys()
  ]);

  const productFamily = findProductFamilyById(productFamilyId);
  const licenseUsage = organizationLicenseUsage.filter(
    x => x.product.family.id === productFamily?.id && x.product.type === ProductType.STUDENT
  );

  const products = fp.flow(
    fp.filter((product: Product) => licenseUsage.some(lu => lu.product.id === product.id)),
    fp.orderBy<Product>('id', ['desc'])
  )(findProductsByProductFamilyIdAndType(productFamilyId, ProductType.STUDENT)) as Product[];

  const showStudentCurriculumSelect = (studentId: number) => {
    if (!showSelectStudentIds.includes(studentId)) {
      setShowSelectStudentIds([...showSelectStudentIds, studentId]);
    }
  };

  const hideStudentCurriculumSelect = (studentId: number) => {
    setShowSelectStudentIds(showSelectStudentIds.filter(id => id !== studentId));
  };

  const handleLicenseAssignClick = (
    productFamilyId: number,
    productCurriculumId: number | '',
    studentId: number
  ) => {
    showStudentCurriculumSelect(studentId);

    if (!productCurriculumId) {
      if (!unselectedCurriculums.includes(studentId)) {
        setUnselectedCurriculums([...unselectedCurriculums, studentId]);
      }
      return;
    }

    onLicenseAssign(productFamilyId, productCurriculumId, studentId);
  };

  const handleLicenseUnassignClick = (
    productFamilyId: number,
    productCurriculumId: number | '',
    studentId: number
  ) => {
    hideStudentCurriculumSelect(studentId);
    setUnselectedCurriculums(unselectedCurriculums.filter(x => studentId !== x));

    if (!productCurriculumId) return;

    onLicenseUnassign(productFamilyId, productCurriculumId, studentId);
  };

  const handleLicenseUnassignAllClick = () => {
    setShowSelectStudentIds([]);
    for (const [studentId, productCurriculumId] of pendingAssignedProductCurriculumMap) {
      onLicenseUnassign(productFamilyId, productCurriculumId, studentId);
    }
  };

  const handleChangeCurriculum = (studentId: number, productCurriculumId: number) => {
    const pendingAssignedCurriculumId = pendingAssignedProductCurriculumMap.get(studentId);
    // If there's already a pending assignment...
    if (pendingAssignedCurriculumId) {
      // Trying to assign the same curriculum, nothing to do.
      if (pendingAssignedCurriculumId === productCurriculumId) return;
      // Trying to assign a curriculum that's different from the existing pending one, so
      // first unassign the existing pending curriculum.
      else onLicenseUnassign(productFamilyId, pendingAssignedCurriculumId, studentId);
    }

    onLicenseAssign(productFamilyId, productCurriculumId, studentId);
  };

  const handleLicenseAssignAllClick = (productCurriculumId: number) => {
    students.forEach(student => handleChangeCurriculum(student.id, productCurriculumId));
    setUnselectedCurriculums([]);
    setShowSelectStudentIds(students.map(s => s.id));
  };

  const handleCurriculumSelect = (
    e: ChangeEvent<{ name?: string | undefined; value: string }>,
    studentId: number
  ) => {
    const value = e.target.value;
    if (!value) return;

    handleChangeCurriculum(studentId, +value);
    setUnselectedCurriculums(unselectedCurriculums.filter(x => studentId !== x));
  };

  const { curriculums, curriculumProductMap } = products.reduce(
    (acc, product) => {
      const productCurriculums = product.curriculums ?? [];
      productCurriculums.forEach(curriculum =>
        acc.curriculumProductMap.set(curriculum.id, product.id)
      );

      return {
        curriculums: [...acc.curriculums, ...productCurriculums],
        curriculumProductMap: acc.curriculumProductMap
      };
    },
    {
      curriculums: [] as ProductCurriculum[],
      curriculumProductMap: new Map<number, number>()
    }
  );

  if (!productFamily) return null;

  const tableData: StudentRowData[] = (fp.flow(
    fp.sortBy<Student>(['lastName', 'firstName']),
    map((s: Student, index: number) => ({
      id: s.id,
      firstName: s.firstName,
      lastName: s.lastName,
      gradeLevel: s.gradeLevel,
      assignedProductCurriculumId: pendingAssignedProductCurriculumMap.get(s.id) ?? '',
      showCurriculumSelect: showSelectStudentIds.includes(s.id),
      hasPendingUpdate: pendingUpdates.some(u => u.studentId === s.id),
      index
    }))
  )(students) as unknown) as StudentRowData[];

  const pendingUpdateResults = products.reduce((acc, product) => {
    const productLicenseUsage = licenseUsage.filter(x => x.product.id === product.id);
    const productPendingUpdates = pendingUpdates.filter(
      u => curriculumProductMap.get(u.productCurriculumId) === product.id
    );

    return acc.set(product.id, getPendingUpdateResult(productLicenseUsage, productPendingUpdates));
  }, new Map<number, PendingUpdateResult>());

  const {
    siteLicensed,
    canAssign,
    showAvailableCount,
    potentialAvailableCountAfterUpdate
  } = getPendingUpdateResult(licenseUsage, pendingUpdates);

  const disabledProductIds = products
    .filter(product => !pendingUpdateResults.get(product.id)?.canAssign)
    .map(product => product.id);

  const licensedCount = pendingAssignedProductCurriculumMap.size;
  const canAssignAll =
    siteLicensed ||
    (products.length === 1 &&
      potentialAvailableCountAfterUpdate >= students.length - licensedCount);

  const handleExpandToggleClick = () => setExpanded(!expanded);

  const { iconUrl, name } = getProductNameAndIcon(productFamily, ProductType.STUDENT);

  return (
    <Paper ref={containerRef} className={cx(classes.root, error && classes.borderFlash)}>
      <Grid container spacing={1} className={classes.container}>
        <Grid item>
          <Box className={classes.updateContainer}>
            {pendingUpdates.length > 0 && <UnsavedChangeIcon />}
          </Box>
          {iconUrl && <img alt="" src={iconUrl} className={classes.productIcon} />}
        </Grid>
        <Grid item container alignContent="flex-start" xs>
          <Grid item xs={12}>
            <Typography variant="h3">{name}</Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="subtitle1" className={classes.licenseCountText}>
              {t('studentsLicensed', { licensedCount, count: students.length })}
            </Typography>
          </Grid>
          {showAvailableCount && (
            <>
              <Divider flexItem orientation="vertical" variant="middle" />
              <Box display="flex" flexDirection="column">
                {products.map(product => {
                  const pendingUpdateResult = pendingUpdateResults.get(product.id);
                  if (!pendingUpdateResult) return null;

                  return (
                    <LicenseCount
                      key={`product_${product.id})_license_count`}
                      product={product}
                      pendingUpdateResult={pendingUpdateResult}
                      singleProduct={products.length === 1}
                    />
                  );
                })}
              </Box>
            </>
          )}
          {!canAssign && (
            <>
              <Divider flexItem orientation="vertical" variant="middle" />
              <Grid item>
                <NoAvailableLicensesMessage />
              </Grid>
            </>
          )}
        </Grid>
        <Grid item className={classes.expanderContainer}>
          <IconButton
            aria-label="toggle button"
            onClick={handleExpandToggleClick}
            className={classes.expanderIcon}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Grid>
        {expanded && (
          <>
            {assignable && (
              <Grid item container spacing={2} xs={12}>
                <Grid item>
                  <AssignAllStudentsButton
                    productFamilyId={productFamilyId}
                    productCurriculums={curriculums}
                    disabled={!canAssignAll}
                    onLicenseAssign={handleLicenseAssignAllClick}
                  />
                </Grid>
                <Grid item>
                  <Button
                    aria-label="Remove all student license button"
                    size="small"
                    variant="contained"
                    color="primary"
                    disableElevation
                    disabled={licensedCount === 0}
                    onClick={handleLicenseUnassignAllClick}
                    className={cx(
                      classes.assignButton,
                      classes.bulkButton,
                      licensedCount === 0 && classes.assignButtonDisabled
                    )}
                    startIcon={<Close />}
                  >
                    {t('Remove licenses from all students')}
                  </Button>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <StudentLicenseTable
                productFamilyId={productFamilyId}
                products={products}
                productCurriculums={curriculums}
                disabledProductIds={disabledProductIds}
                assignable={assignable}
                canAssign={canAssign}
                onLicenseAssign={handleLicenseAssignClick}
                onLicenseUnassign={handleLicenseUnassignClick}
                onCurriculumSelect={handleCurriculumSelect}
                showCurriculumSelect={showStudentCurriculumSelect}
                hideCurriculumSelect={hideStudentCurriculumSelect}
                data={tableData}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};

const LicenseCount = ({
  product,
  pendingUpdateResult,
  singleProduct = false
}: LicenseCountProps) => {
  const classes = useStyles();
  const { t } = useTranslation('ClassroomProductLicenseCard');

  return (
    <Typography
      key={`product_${product.id}_count`}
      variant="subtitle1"
      className={classes.licenseCountText}
    >
      {pendingUpdateResult.willUseOverage
        ? t(singleProduct ? 'overageLicenses' : 'overageProductLicenses', {
            count: pendingUpdateResult.potentialAvailableCountAfterUpdate,
            productName: product.name
          })
        : t(singleProduct ? 'availableLicenses' : 'availableProductLicenses', {
            count: pendingUpdateResult.realAvailableCountAfterUpdate,
            productName: product.name
          })}
    </Typography>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: '2px solid transparent',
    padding: theme.spacing(2)
  },
  rootAssigned: {
    background: '#E7EFEA'
  },
  borderFlash: {
    animation: `$flash 1000ms ${theme.transitions.easing.easeInOut} 0s 3`
  },
  '@keyframes flash': {
    '0%': {
      border: '2px solid inherit'
    },
    '50%': {
      border: `2px solid ${theme.palette.error.light}`
    },
    '100%': {
      border: '2px solid inherit'
    }
  },
  expanderContainer: {
    alignSelf: 'center'
  },
  expanderIcon: {
    color: '#006DB8'
  },
  container: { position: 'relative' },
  productIcon: {
    padding: theme.spacing(1),
    height: theme.typography.pxToRem(80),
    width: theme.typography.pxToRem(80)
  },
  productLabelContainer: {
    paddingRight: theme.spacing(1)
  },
  updateContainer: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  changeIcon: {
    position: 'absolute',
    left: 15
  },
  statusContainer: {
    height: '100%',
    paddingBottom: theme.spacing(1)
  },
  assignedText: {
    color: theme.palette.success.dark
  },
  bulkButton: {
    padding: theme.typography.pxToRem(20)
  },
  assignedIcon: {
    bottom: theme.typography.pxToRem(-4),
    fill: theme.palette.success.dark,
    fontSize: theme.typography.pxToRem(18),
    position: 'relative'
  },
  unassignedText: {
    color: '#696969'
  },
  licenseCountText: {
    fontWeight: 300
  },
  assignButton: {
    background: '#FFFFFF',
    border: '1px solid #969696',
    color: '#2D2D2D',
    '&:hover': {
      background: '#FFFFFF'
    }
  },
  assignButtonDisabled: {
    backgroundColor: '#FFFFFF !important',
    boxShadow: 'none !important',
    color: 'rgba(0, 0, 0, 0.26) !important'
  },
  infoIcon: {
    cursor: 'pointer',
    fontSize: theme.typography.pxToRem(24)
  },
  actionCell: {
    width: 75
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  licenseStatusContainer: {
    lineHeight: '1rem'
  },
  licenseStatusContainerAssigned: {
    fill: theme.palette.success.dark,
    color: theme.palette.success.dark
  },
  assignedIconContainer: {
    paddingRight: theme.typography.pxToRem(4)
  },
  actionButton: {
    background: '#FFFFFF',
    border: '1px solid #969696',
    color: '#2D2D2D',
    padding: theme.typography.pxToRem(20),
    '&:hover': {
      background: '#FFFFFF'
    }
  },
  actionButtonDisabled: {
    backgroundColor: '#FFFFFF !important',
    boxShadow: 'none !important',
    color: 'rgba(0, 0, 0, 0.26) !important'
  }
}));

export default ClassroomProductLicenseCard;
